import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { ARROW_STEPS } from '../arrow-steps.ts';
import { usePlacementUnder, type PopupAlign } from '../popup-placement.ts';

const MENU_ITEMS = '[role^="menuitem"]';

const itemsOf = (list: HTMLElement | null): HTMLElement[] => [
  ...(list?.querySelectorAll<HTMLElement>(MENU_ITEMS) ?? []),
];

const focusItem = (list: HTMLElement | null, index: number): void => {
  const items = itemsOf(list);
  if (items.length === 0) return;
  items[(index + items.length) % items.length]?.focus();
};

export const useMenuPopover = (focusOnOpen: number, align: PopupAlign = 'start') => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const placed = usePlacementUnder(root, open, align);
  const place = { ...placed, width: undefined };

  useEffect(() => {
    if (!open) return undefined;
    focusItem(list.current, Math.max(focusOnOpen, 0));
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
    };
  }, [open, focusOnOpen]);

  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const moveWithKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    const step = ARROW_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const items = itemsOf(list.current);
    focusItem(list.current, items.findIndex((item) => item === document.activeElement) + step);
  };

  const toggle = () => {
    setOpen((current) => !current);
  };

  return { open, toggle, close, menuId, root, list, trigger, moveWithKeys, place };
};
